import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isFirstTimeUser } from '@/utils/welcomeUtils';

interface RequireWelcomeProps {
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Component that protects routes by redirecting first-time users to the welcome page
 * 
 * Usage:
 * <RequireWelcome>
 *   <YourProtectedContent />
 * </RequireWelcome>
 */
export default function RequireWelcome({ 
  children, 
  redirectTo = '/welcome' 
}: RequireWelcomeProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [needsWelcome, setNeedsWelcome] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    // Check if user has completed the welcome flow
    const firstTimeUser = isFirstTimeUser();
    
    setNeedsWelcome(firstTimeUser);
    setIsLoading(false);
  }, [location.pathname]);
  
  // While checking, show nothing to avoid flashing content
  if (isLoading) {
    return null;
  }
  
  // If the user is a first-time visitor, redirect to welcome page
  if (needsWelcome) {
    return <Navigate to={redirectTo} replace />;
  }
  
  // Otherwise, render the protected content
  return <>{children}</>;
} 